<template>
    <v-row class="justify-center align-center mt-2 mx-4">
        <v-card class="col-7">
            <v-card-title>{{ title }}</v-card-title>
            <v-card-text class="px-5">
                <v-col align-self="center">
                    <v-row>
                        <v-text-field :label="$t('Name')" v-model="variable.name" :error-messages="nameErrors"
                            @blur="$v.variable.name.$touch()"
                            :maxLength="50" @input="updateCodeValue($v)"
                            :disabled="variable.id"
                        ></v-text-field>
                    </v-row>
                    <v-row>
                        <v-text-field :label="$t('Code')" v-model="variable.code" :error-messages="nameErrors"
                                      @input="$v.variable.code.$touch()" @blur="$v.variable.code.$touch()"
                                      :maxLength="80"
                                      readonly
                                      :disabled="variable.id"
                        ></v-text-field>
                    </v-row>
                    <v-row>
                        <v-select
                            :label="$t('Type')"
                            :items="typeList"
                            :disabled="variable.id"
                            v-model="variable.type"
                            item-text="text"
                            item-value="id"
                            @change="changeType"
                        ></v-select>
                    </v-row>
                    <v-row style="display: block;">
                        <v-text-field v-if="variable.type === FieldType.String"
                            :label="$t('Value')"
                            v-model="variable.value"
                            :error-messages="valueErrors"
                            :maxLength="80"
                            @input="$v.variable.value.$touch()"
                            @blur="$v.variable.value.$touch()"
                        ></v-text-field>
                        <DatePickerISOCompliant v-if="variable.type === FieldType.DateTime"
                            :errorMessages="valueErrors"
                            v-model="variable.value"
                            clearable
                            @input="$v.variable.value.$touch()"
                            @change="$v.variable.value.$touch()"
                        ></DatePickerISOCompliant>
                        <SearchCriteriaNumericInputISO v-if="variable.type === FieldType.Numeric"
                            :scale="6"
                            :labelText="$t('Value')"
                            :conditionItem="variable"
                            :errorMessages="valueErrors"
                            :isForSearchCriteria="false"
                            @input="$v.variable.value.$touch()"
                            @blur="$v.variable.value.$touch()"
                        ></SearchCriteriaNumericInputISO>
                    </v-row>
                </v-col>
                <v-overlay :value="loading" :absolute="true">
                    <div class="text-center">
                        <v-progress-circular class="my-4" :size="50" width="5" color="primary"
                            indeterminate></v-progress-circular>
                    </div>
                </v-overlay>
                <v-row class="justify-end">
                    <v-btn @click="cancel()">{{ $t('Cancel') }}</v-btn>
                    <v-btn @click="saveItem" class="primary">{{ $t('Save') }}</v-btn>
                </v-row>
            </v-card-text>
        </v-card>
    </v-row>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import Utils from '@/ecm/common/Utils';
import Variable from '@/ecm/models/variables/Variable';
import DatePickerISOCompliant from '@/components/base/DatePickerISOCompliant.vue';
import SearchCriteriaNumericInputISO from '@/components/search/inputs/SearchCriteriaNumericInputISO.vue';
import VariableService from '@/services/VariableService';
import { VariableType } from '@/ecm/variables/VariableType';
import FieldType from '@/ecm/variables/FieldType';
import snakeCase from 'lodash/snakeCase';
import { VAR_PREFIX } from '../../../ecm/common/Constants';

export default {
    name: 'Variable',
    components: {
        SearchCriteriaNumericInputISO,
        DatePickerISOCompliant
    },
    async created() {
        await this.getVariable();
        this.$v.$reset();
    },
    data() {
        return {
            variable: new Variable(),
            isNew: true,
            loading: false,
            typeList: VariableType.map(item => ({
                ...item,
                text: this.$t(item.text)
            })),
            FieldType: FieldType
        };
    },
    mixins: [validationMixin],
    validations: {
        variable: {
            name: { required },
            code: { required },
            value: { required },
        },
    },
    computed: {
        title() {
            return (
                this.$t('Variable') + (this.variable.name ? ' - ' + this.variable.name : '')
            );
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.variable.name.$dirty) return errors;
            !this.$v.variable.name.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        valueErrors() {
            const errors = [];
            if (!this.$v.variable.value.$dirty) return errors;
            !this.$v.variable.value.required && errors.push(this.$t('RequiredField'));
            return errors;
        }
    },
    methods: {
        async getVariable() {
            this.loading = true;
            if (isNaN(this.$route.params.id) || this.$route.params.id < 1) {
                this.variable = new Variable();
            } else {
                this.isNew = false;
                await VariableService.get(this.$route.params.id).then((data) => {
                    if (data === null) {
                        Utils.toast.error(`${this.$t('ItemNotFoundException')} (${this.$t('Variable')}: ${this.$route.params.id})`);
                        this.$router.push({
                            name: 'Variables'
                        });
                    }
                    this.variable = new Variable(data);
                });
            }
            this.loading = false;
        },
        updateCodeValue(v) {
            if (!this.variable.name) {
                this.variable.code = null;
                return;
            }

            this.variable.code = VAR_PREFIX + snakeCase(this.variable.name).substring(0, 80).toUpperCase();
            v.variable.name.$touch();
        },
        changeType() {
            this.variable.value = '';
        },
        saveItem() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.loading = true;
            if (this.isNew) {
                this.variable.id = 0;
                VariableService.create(this.variable)
                    .then((result) => {
                        if (result) {
                            Utils.toast.success(this.$t('Variable') + ' ' + this.$t('CreatedSuccessfullyFem'));
                            this.$router.push({
                                name: 'Variables'
                            });
                        }
                    }).catch(() => {
                        // error
                    })
                    .then(() => {
                        this.loading = false;
                    });
            } else {
                VariableService.update(this.variable, this.$route.params.id)
                    .then(() => {
                        Utils.toast.success(this.$t('Variable') + ' ' + this.$t('UpdatedSuccessfullyFem'));
                        this.$router.push({
                            name: 'Variables'
                        });
                    }).catch(() => {
                        // error
                    }).then(() => {
                        this.loading = false;
                    });
            }
        },
        cancel() {
            this.$router.push({
                name: 'Variables',
            });
        }
    },
};
</script>
